import React, { useState } from "react";
import AllFlowsContactInfoUpdate from "./AllFlowsContactInfoUpdate";
import RegisterCredentials from "./RegisterCredentials";
import { useForm } from "react-hook-form";
import { useTranslations } from "../../queries";
import SubmitButton from "./SubmitButton";
import { useMutation } from "@tanstack/react-query";
import { getUpdateContactInfoJson } from "./helpers";
import { registerUser, updateContact } from "../../services/accountSetup/accountSetupService";
import type { SetupTranslations, SaveErrorData, SaveErrorState, RegisterFields } from "./types";
import useRedirect from "../../hooks/useRedirect";

const AccountSetup = () => {
    const {
        register,
        handleSubmit,
        getValues,
        formState: { errors },
        trigger
    } = useForm<RegisterFields>({
        criteriaMode: "all",
        mode: "all"
    });
    const { accountSetup }: { accountSetup: SetupTranslations } = useTranslations();
    const [saveError, setSaveError] = useState<SaveErrorState | null>(null);
    const updateContactMutation = useMutation({
        mutationFn: updateContact,
        onError: (err: SaveErrorData) => {
            const errData = err.data.error;
            setSaveError(errData);
        }
    });
    const registrationMutation = useMutation({
        mutationFn: registerUser
    });
    const redirect = useRedirect();

    const setupAccount = async (data) => {
        const flowName = "registrationFlow";
        const contactParams = getUpdateContactInfoJson(data, flowName);
        const registerParams = {
            username: data.username,
            password: data.password,
            flowName
        };

        try {
            await updateContactMutation.mutateAsync(contactParams);
            await registrationMutation.mutateAsync(registerParams);
        } catch (e) {
            console.error(e);
        } finally {
            redirect(data);
        }
    };

    const showSpinner =
        updateContactMutation.status === "pending" || registrationMutation.status === "pending";

    return (
        <div className="container row">
            <div className="col-md-6 col-md-offset-3 col-sm-8 col-sm-offset-2 registration">
                <header className="contained-form-header">
                    <h1>{accountSetup.setUpAccountTitle}</h1>
                    <p>{accountSetup.completeRegistrationInfo}</p>
                </header>

                <div className="inner-container with-padding with-shadow">
                    <form
                        role="form"
                        id="registration-form"
                        name="accountSetupForm"
                        className="clearfix"
                        onSubmit={handleSubmit(setupAccount)}
                        noValidate
                        autoComplete="off"
                    >
                        <h2>{accountSetup.contactInformationTitle}</h2>

                        <AllFlowsContactInfoUpdate
                            register={register}
                            trigger={trigger}
                            errors={errors}
                            saveError={saveError}
                        />

                        <hr />
                        <h2> {accountSetup.usernamePasswordTitle}</h2>

                        <RegisterCredentials
                            register={register}
                            getValues={getValues}
                            trigger={trigger}
                            errors={errors}
                        />

                        <div className="form-group clearfix">
                            <SubmitButton showSpinner={showSpinner} />
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AccountSetup;
